// ============================================================================
// THE CYCLE — CountUp Animation Component
// Animates a number from 0 to its final value using requestAnimationFrame.
// ============================================================================
import { useState, useEffect, useRef } from 'react';

/**
 * CountUp — animates a numeric value from 0 (or startVal) to endVal.
 * @param {number} endVal — target value
 * @param {number} [duration=800] — animation duration in ms
 * @param {number} [decimals=0] — decimal places to show
 * @param {string} [prefix=''] — prefix string (e.g., '$')
 * @param {string} [suffix=''] — suffix string (e.g., '%')
 * @param {boolean} [stripLeadingZero=false] — strip leading zero for batting averages
 * @param {number} [startVal=0] — starting value
 * @returns {string} — formatted animated value
 */
const CountUp = ({
  endVal,
  duration = 800,
  decimals = 0,
  prefix = '',
  suffix = '',
  stripLeadingZero = false,
  startVal = 0,
}) => {
  const [display, setDisplay] = useState(format(startVal, decimals, prefix, suffix, stripLeadingZero));
  const frameRef = useRef(null);
  const startTimeRef = useRef(null);

  useEffect(() => {
    const end = parseFloat(endVal);
    const start = parseFloat(startVal);
    if (isNaN(end)) { setDisplay('—'); return; }

    const animate = (timestamp) => {
      if (!startTimeRef.current) startTimeRef.current = timestamp;
      const elapsed = timestamp - startTimeRef.current;
      const progress = Math.min(elapsed / duration, 1);
      // Ease-out cubic
      const eased = 1 - Math.pow(1 - progress, 3);
      const current = start + (end - start) * eased;
      setDisplay(format(current, decimals, prefix, suffix, stripLeadingZero));
      if (progress < 1) {
        frameRef.current = requestAnimationFrame(animate);
      }
    };

    startTimeRef.current = null;
    frameRef.current = requestAnimationFrame(animate);

    return () => {
      if (frameRef.current) cancelAnimationFrame(frameRef.current);
    };
  }, [endVal, duration, decimals, prefix, suffix, stripLeadingZero, startVal]);

  return display;
};

function format(val, decimals, prefix, suffix, stripLeadingZero) {
  let str = parseFloat(val).toFixed(decimals);
  if (stripLeadingZero) str = str.replace(/^0/, '');
  return `${prefix}${str}${suffix}`;
}

export default CountUp;
